import React, { useState, useEffect } from 'react';
import { userAPI, authAPI } from '../services/api';

const EmployeeSummary = () => {
  const [employees, setEmployees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'employee',
    billingRate: 0
  });

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await userAPI.getAll();
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      alert('Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await authAPI.register(formData);
      alert('Employee registered successfully');
      setFormData({
        email: '',
        password: '',
        repeatpassword: '',
        firstName: '',
        lastName: '',
        role: 'employee',
        billingRate: 0
      });
      setShowForm(false);
      fetchEmployees();
    } catch (error) {
      alert(`Error: ${error.response?.data?.error || 'Failed to register employee'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBillingRate = async (userId, newRate) => {
    try {
      await userAPI.update(userId, { billingRate: parseFloat(newRate) });
      alert('Billing rate updated successfully');
      fetchEmployees();
    } catch (error) {
      alert(`Error: ${error.response?.data?.error || 'Failed to update billing rate'}`);
    }
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this employee?')) return;

    try {
      await userAPI.delete(userId);
      alert('Employee deactivated successfully');
      fetchEmployees();
    } catch (error) {
      alert(`Error: ${error.response?.data?.error || 'Failed to deactivate employee'}`);
    }
  };

  const viewBillingSummary = async (userId, employeeName) => {
    try {
      const response = await userAPI.getBillingSummary(userId);
      const summary = response.data;

      alert(
        `Billing Summary for ${employeeName}\n` +
        `Billing Rate: $${summary.user.billingRate}/hour\n` +
        `Total Hours: ${summary.summary.totalHours}\n` +
        `Total Earnings: $${summary.summary.totalEarnings}\n` +
        `Timesheets: ${summary.summary.timesheetCount}\n\n` +
        `Projects:\n` +
        Object.entries(summary.projectBreakdown).map(([project, data]) =>
          `- ${project}: ${data.hours} hours ($${data.earnings})`
        ).join('\n')
      );
    } catch (error) {
      alert('Failed to fetch billing summary');
    }
  };

  return (
    <div className="employee-summary">
      <div className="section-header">
        <h3>Employee Management</h3>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : '+ Add Employee'}
        </button>
      </div>

      {showForm && (
        <div className="employee-form">
          <h4>Register New Employee</h4>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  minLength="8"
                  required
                />
              </div>

              <div className="form-group">
                <label>repeatpassword *</label>
                <input
                  type="same password"
                  value={formData.repeatpassword}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  minLength="8"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  required
                >
                  <option value="employee">Employee</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="form-group">
                <label>Billing Rate ($/hour)</label>
                <input
                  type="number"
                  value={formData.billingRate}
                  onChange={(e) => setFormData({...formData, billingRate: parseFloat(e.target.value) || 0})}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Registering...' : 'Register Employee'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowForm(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="employees-list">
        {loading ? (
          <div className="loading">Loading employees...</div>
        ) : employees.length === 0 ? (
          <div className="empty-state">No employees found.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Billing Rate</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.map(employee => (
                <tr key={employee._id}>
                  <td>{employee.firstName} {employee.lastName}</td>
                  <td>{employee.email}</td>
                  <td>
                    <span className={`role-badge ${employee.role}`}>
                      {employee.role}
                    </span>
                  </td>
                  <td>
                    <div className="billing-rate-cell">
                      $<input
                        type="number"
                        value={employee.billingRate}
                        onChange={(e) => {
                          const newEmployees = [...employees];
                          const index = newEmployees.findIndex(e => e._id === employee._id);
                          newEmployees[index].billingRate = e.target.value;
                          setEmployees(newEmployees);
                        }}
                        onBlur={(e) => handleUpdateBillingRate(employee._id, e.target.value)}
                        className="billing-rate-input"
                        step="0.01"
                        min="0"
                      />
                      /hour
                    </div>
                  </td>
                  <td>
                    <span className={`status ${employee.isActive ? 'active' : 'inactive'}`}>
                      {employee.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="action-buttons">
                    <button
                      className="btn btn-sm btn-info"
                      onClick={() => viewBillingSummary(employee._id, `${employee.firstName} ${employee.lastName}`)}
                      title="View Billing Summary"
                    >
                      Billing
                    </button>
                    {employee.isActive && (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeactivate(employee._id)}
                        title="Deactivate"
                      >
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default EmployeeSummary;
